import { LayoutUsage } from '@barthauer/abc/arena';
import { CompareHelper, ConvertHelper, ProfileInfoViewModel, ProfileSettingsViewModel } from '@barthauer/abc/core';
import { ProfileInfo } from '@barthauer/abc/core/authentication';
import { ObjectSpaceService, ObjectTypeInfoViewModel } from '@barthauer/abc/core/data';
import { DataType } from 'devextreme/common';
import { convertDataType } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { Observable, of, Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import {
  ArenaLayout,
  ArenaSettings,
  MapInfo,
  MapInfoCommand,
  MapInfoGeometryDefinition,
  MapInfoObjectType,
  MapInfoObjectTypeColumn,
} from '../models/mapsData.model';

export class MapInfoViewModel {
  private _objectTypes: MapInfoObjectTypeViewModel[];

  get isReadOnly(): boolean {
    return ConvertHelper.toBoolean(this.mapInfo.IsReadOnly);
  }
  get name(): string {
    return this.mapInfo.Name;
  }
  get objectTypes() {
    if (!this._objectTypes) {
      this._objectTypes = [];
      for (const objectType of this.mapInfo.ObjectTypes ?? []) {
        if (!objectType.ObjectSets || objectType.ObjectSets.length === 0) {
          this._objectTypes.push(
            new MapInfoObjectTypeViewModel(
              objectType,
              objectType.Name,
              objectType.Commands,
              objectType.Points,
              objectType.Lines,
              objectType.Polygons,
            ),
          );
        } else {
          for (const objectSet of objectType.ObjectSets) {
            this._objectTypes.push(
              new MapInfoObjectTypeViewModel(
                objectSet,
                objectType.Name,
                objectType.Commands,
                objectType.Points,
                objectType.Lines,
                objectType.Polygons,
              ),
            );
          }
        }
      }
      for (const ds of this.mapInfo.DataSets ?? []) {
        this._objectTypes.push(new MapInfoObjectTypeViewModel(new MapInfoObjectType(ds), null, [], [], [], []));
      }
    }
    return this._objectTypes;
  }

  get objectSpaceObjectTypes() {
    const hasObjectSets = this.objectTypes.findIndex((_) => _.objectType && _.objectSetName) >= 0;
    return this.objectTypes.filter((_) => _.objectType && (!hasObjectSets || _.objectSetName));
  }

  get dataSets() {
    return this.objectTypes.filter((_) => !_.objectType);
  }

  constructor(private mapInfo: MapInfo) {}

  initialize() {
    if (this._objectTypes) {
      for (const ot of this._objectTypes) {
        ot.fillDefaultDisplayText();
      }

      let dup: MapInfoObjectTypeViewModel[] = MapInfoViewModel.findDuplicateObjectTypes(this._objectTypes);
      let maxLength = 0;
      for (const ot of dup) {
        const l = ot.objectSetName?.split('\\').length;
        if (l > maxLength) {
          maxLength = l;
        }
      }
      for (let i = 1; i < maxLength; i++) {
        let texts = dup.map((_) => _.displayText);
        texts = texts.filter((item, pos) => texts.indexOf(item) === pos);
        for (const text of texts) {
          const candidates = dup.filter((_) => _.displayText === text);
          if (candidates.length > 1) {
            for (const ot of candidates) {
              const parts = ot.objectSetName?.split('\\');
              if (parts?.length - 1 - i >= 0) {
                ot.displayTextSuffixes.push(parts[parts.length - 1 - i]);
              }
            }
          }
        }
      }

      dup = MapInfoViewModel.findDuplicateObjectTypes(this._objectTypes);
      for (const ot of dup) {
        const suffix = this.translateNamespace(ot.objectType);
        if (ot.displayTextSuffixes.indexOf(suffix) < 0) {
          ot.displayTextSuffixes.push(suffix);
        }
      }

      this._objectTypes = this._objectTypes.sort((one, two) => (one.displayText > two.displayText ? 1 : -1));
    }
  }

  private static findDuplicateObjectTypes(arr: MapInfoObjectTypeViewModel[]): MapInfoObjectTypeViewModel[] {
    const sorted_arr = arr.slice().sort((one, two) => (one.displayText > two.displayText ? 1 : -1));

    const results = [];
    for (let i = 0; i < sorted_arr.length - 1; i++) {
      for (let a = 0; a < sorted_arr.length - 1; a++) {
        if (
          sorted_arr[i] !== sorted_arr[a] &&
          !results.some((x) => x === sorted_arr[i]) &&
          sorted_arr[i].displayText === sorted_arr[a].displayText
        ) {
          results.push(sorted_arr[i]);
        }
      }
    }
    return results;
  }

  private translateNamespace(ot: ObjectTypeInfoViewModel) {
    let namespaceDisplayText: string;
    switch (ot.namespace) {
      case 'Common':
        namespaceDisplayText = 'Allgemein';
        break;
      case 'Common.Custom':
        namespaceDisplayText = 'Benutzerdefiniert';
        break;
      case 'Common.EconomicComparison':
        namespaceDisplayText = 'Kostenvergleich';
        break;
      case 'Common.GeoObjects':
        namespaceDisplayText = 'GeoObjekte';
        break;
      case 'Common.Maintenance':
        namespaceDisplayText = 'Wartung';
        break;
      case 'Common.UniWert':
        namespaceDisplayText = 'UniWERT';
        break;
      case 'Energy':
        namespaceDisplayText = 'Kabel';
        break;
      case 'Gas':
        namespaceDisplayText = 'Gas';
        break;
      case 'Grundstuecke':
        namespaceDisplayText = 'Grundstücke';
        break;
      case 'Planting':
        namespaceDisplayText = 'Baum';
        break;
      case 'Street':
        namespaceDisplayText = 'Straße';
        break;
      case 'WasteWater':
        namespaceDisplayText = 'Abwasser';
        break;
      case 'WasteWater.Environment':
        namespaceDisplayText = 'Betriebsdaten';
        break;
      case 'WasteWater.IndirectDischarger':
        namespaceDisplayText = 'Indirekteinleiter';
        break;
      case 'WasteWater.Treatment':
        namespaceDisplayText = 'Kleinkläranlage';
        break;
      case 'Water':
        namespaceDisplayText = 'Wasser';
        break;
      default:
        namespaceDisplayText = ot.namespace;
        break;
    }
    return namespaceDisplayText;
  }
}
export class MapInfoObjectTypeViewModel {
  private _columns: MapInfoObjectTypeColumnViewModel[];
  private _commands: MapInfoCommandViewModel[];
  private _points: MapInfoGeometryDefinitionViewModel[];
  private _lines: MapInfoGeometryDefinitionViewModel[];
  private _polygons: MapInfoGeometryDefinitionViewModel[];
  private _objectType: ObjectTypeInfoViewModel;
  private _displayText: string;
  private _displayTextSuffixes: string[] = [];
  get name(): string {
    return this.objectTypeName ?? this.mapInfoObjectType.Name;
  }
  get objectSetName(): string {
    if (
      this.mapInfoObjectType &&
      (this.objectTypeName != this.mapInfoObjectType.Name || this.mapInfoObjectType.DisplayName)
    ) {
      return this.mapInfoObjectType.DisplayName ?? this.mapInfoObjectType.Name;
    }
    return null;
  }
  get identityColumnName(): string {
    return this.mapInfoObjectType?.IdentityColumnName;
  }
  get objectType(): ObjectTypeInfoViewModel {
    return this._objectType;
  }
  get dataSourceId(): string {
    return this.mapInfoObjectType?.DataSourceId;
  }
  set objectType(val: ObjectTypeInfoViewModel) {
    this._objectType = val;
  }
  get displayText(): string {
    let result = this._displayText;
    if (this.displayTextSuffixes?.length) {
      result += ' (';
      for (let i = this.displayTextSuffixes.length - 1; i >= 0; i--) {
        result += this.displayTextSuffixes[i];
        if (i > 0) {
          result += ' | ';
        }
      }
      result += ')';
    }
    return result;
  }
  get displayTextSuffixes(): string[] {
    return this._displayTextSuffixes;
  }
  get columns() {
    if (!this._columns) {
      this._columns = [];
      for (const column of this.mapInfoObjectType.Columns) {
        this._columns.push(new MapInfoObjectTypeColumnViewModel(column));
      }
    }
    return this._columns;
  }
  get commands() {
    if (!this._commands) {
      this._commands = [];
      for (const action of this.mapInfoCommands) {
        this._commands.push(new MapInfoCommandViewModel(action));
      }
    }
    return this._commands;
  }
  get points() {
    if (!this._points) {
      this._points = [];
      for (const g of this.mapInfoPoints) {
        this._points.push(new MapInfoGeometryDefinitionViewModel(g));
      }
    }
    return this._points;
  }
  get lines() {
    if (!this._lines) {
      this._lines = [];
      for (const g of this.mapInfoLines) {
        this._lines.push(new MapInfoGeometryDefinitionViewModel(g));
      }
    }
    return this._lines;
  }
  get polygons() {
    if (!this._polygons) {
      this._polygons = [];
      for (const g of this.mapInfoPolygons) {
        this._polygons.push(new MapInfoGeometryDefinitionViewModel(g));
      }
    }
    return this._polygons;
  }
  constructor(
    private mapInfoObjectType: MapInfoObjectType,
    private objectTypeName: string,
    private mapInfoCommands: MapInfoCommand[],
    private mapInfoPoints: MapInfoGeometryDefinition[],
    private mapInfoLines: MapInfoGeometryDefinition[],
    private mapInfoPolygons: MapInfoGeometryDefinition[],
  ) {}

  fillDefaultDisplayText() {
    let displayText = null;
    if (this.objectSetName) {
      if (this.objectType) {
        displayText = this.objectType.prepareDisplayTextPlural(this.objectSetName);
      } else {
        displayText = this.objectSetName;
      }
    } else if (!displayText) {
      switch (this.name) {
        case 'EnKabel':
          displayText = 'Fernmeldekabel';
          break;
        case 'PiPipeJoinMethod':
          displayText = 'Schweissnähte';
          break;
        case 'PiPipeSegment':
          displayText = 'Rohrsegmente';
          break;
        case 'PiSleeve':
          displayText = 'Muffen';
          break;
        case 'PiSeries':
          displayText = 'Transportleitungen';
          break;
        case 'PiSite':
          displayText = 'Nebenzeichnungen (Site)';
          break;
        case 'PiValve':
          displayText = 'Armaturen';
          break;
        case 'PiVent':
          displayText = 'Lüfter';
          break;
        case 'PiInsolatedFlange':
          displayText = 'Flansche';
          break;
        case 'PiControlPoint':
          displayText = 'Kontrollpunkte';
          break;
        case 'PiLocalisedDocument':
          displayText = 'Bestandspläne';
          break;
        case 'PiLevelPoint':
          displayText = 'Pegel';
          break;
        case 'PiGWMonitoringPoint':
          displayText = 'Grundwassermessstellen';
          break;
        case 'PiNode':
          displayText = 'Rohrverbindungen';
          break;
        case 'PiEmptying':
          displayText = 'Entleerungen';
          break;
        case 'WaMobileArmatur':
          displayText = 'Standrohre';
          break;
        default:
          displayText = this.objectType?.displayTextPlural;
          break;
      }
    }
    if (!displayText || displayText === this.name) {
      displayText = this.name.substring(this.name.lastIndexOf('\\') + 1);
    }
    this._displayText = displayText;
  }

  public isMatch(name: string): boolean {
    let match = this.objectSetName === name || this.name == name;
    if (!match) {
      match = this.mapInfoObjectType.Templates.find((_) => _.Name === name) != null;
    }
    return match;
  }
}

export class MapInfoGeometryDefinitionViewModel {
  get identifier(): string {
    return this.geometry.Identifier;
  }
  get canAdd(): boolean {
    return this.geometry.CanAdd;
  }
  constructor(private geometry: MapInfoGeometryDefinition) {}
}

export class MapInfoObjectTypeColumnViewModel {
  get name(): string {
    return this.column.Name;
  }
  get displayName(): string {
    return this.column.DisplayName;
  }
  get dataType(): DataType {
    return convertDataType(this.column.DataType);
  }
  get editMask(): string {
    return this.column.EditMask;
  }
  get sortOrder(): string {
    return this.column.SortOrder;
  }
  constructor(private column: MapInfoObjectTypeColumn) {}
}

export class MapInfoCommandViewModel {
  get name(): string {
    return this.command.Name;
  }
  get displayText(): string {
    return this.command.DisplayText;
  }
  get inputType(): string {
    return this.command.InputType;
  }
  get windowType(): string {
    return this.command.WindowType;
  }
  get urlPattern(): string {
    return this.command.UrlPattern;
  }
  get svgImage(): string {
    return this.command.SvgImage;
  }
  get badgeValueExpression(): string {
    return this.command.BadgeValueExpression;
  }
  get enabledExpression(): string {
    return this.command.EnabledExpression;
  }
  get enabledByBadgeValue(): boolean {
    return this.command.EnabledByBadgeValue;
  }
  get visibility(): string {
    return this.command.Visibility;
  }
  constructor(private command: MapInfoCommand) {}
}

export class ArenaSettingsViewModel {
  private _layouts: ArenaLayoutViewModel[];
  private findArenaLayoutSubject = new Subject<{ arenaName: string; objectTypeName: string; usage: LayoutUsage }>();
  get defaultName(): string {
    return this.settings?.defaultName;
  }
  get definitionId(): string {
    return this.settings?.definitionId;
  }
  get isReadOnly(): boolean {
    return CompareHelper.isNullorEmpty(this.settings?.isReadOnly)
      ? null
      : ConvertHelper.toBoolean(this.settings?.isReadOnly);
  }
  get layouts(): ArenaLayoutViewModel[] {
    if (!this._layouts) {
      this._layouts = new Array<ArenaLayoutViewModel>();
      if (this.settings) {
        for (const layout of this.settings.Layouts) {
          this._layouts.push(new ArenaLayoutViewModel(layout));
        }
      }
    }
    return this._layouts;
  }

  constructor(
    private settings: ArenaSettings,
    private oss: ObjectSpaceService,
  ) {}

  private findArenaLayoutObservable(
    arenaName: string,
    objectTypeName: string,
    usage: LayoutUsage,
  ): Observable<{ arenaName: string; layout: ArenaLayoutViewModel }> {
    let ret: Observable<{ arenaName: string; layout: ArenaLayoutViewModel }>;
    let resultLayout: ArenaLayoutViewModel;
    if (this.layouts && this.layouts.length > 0) {
      resultLayout = this.layouts.find((l) => l.objectTypeName === objectTypeName && l.usage === usage);
    }
    if (resultLayout) {
      ret = of({ arenaName: arenaName, layout: resultLayout });
    } else {
      ret = this.oss
        .getObjectType(objectTypeName)
        .pipe(
          concatMap((x) => (x.baseType ? this.findArenaLayoutObservable(arenaName, x.baseType.name, usage) : of(null))),
        );
    }
    return ret;
  }
  findArenaLayout(arenaName: string, objectTypeName: string, usage: LayoutUsage) {
    this.findArenaLayoutSubject.next({ arenaName: arenaName, objectTypeName: objectTypeName, usage: usage });
  }

  onFindArenaLayout(): Observable<{ arenaName: string; layout: ArenaLayoutViewModel }> {
    return this.findArenaLayoutSubject
      .asObservable()
      .pipe(
        concatMap((layoutQuery) =>
          this.findArenaLayoutObservable(layoutQuery.arenaName, layoutQuery.objectTypeName, layoutQuery.usage),
        ),
      );
  }
}

export class ArenaLayoutViewModel {
  get objectTypeName(): string {
    return this.layout.objectTypeName;
  }
  get usage(): LayoutUsage {
    let ret: LayoutUsage = LayoutUsage.Standard;
    switch (this.layout.usage) {
      case 'New':
        ret = LayoutUsage.New;
        break;
    }
    return ret;
  }
  get fullName(): string {
    return this.layout.fullName;
  }
  constructor(private layout: ArenaLayout) {}
}

export class ProfileInfoWithArenaViewModel extends ProfileInfoViewModel {
  get settings(): ProfileSettingsWithArenaViewModel {
    return <ProfileSettingsWithArenaViewModel>super.settings;
  }

  constructor(profileInfo: ProfileInfo, oss: ObjectSpaceService) {
    let settings: ProfileSettingsWithArenaViewModel;
    if (profileInfo.settings) {
      settings = new ProfileSettingsWithArenaViewModel(profileInfo.settings, oss);
    }
    super(profileInfo, settings);
  }
}

export class ProfileSettingsWithArenaViewModel extends ProfileSettingsViewModel {
  private _arena: ArenaSettingsViewModel;
  get arena(): ArenaSettingsViewModel {
    return this._arena;
  }

  constructor(settings: any, oss: ObjectSpaceService) {
    super(settings);

    if (settings.Arena) {
      this._arena = new ArenaSettingsViewModel(new ArenaSettings(settings.Arena), oss);
    }
  }
}
